import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CircleCheck } from 'lucide-react'
import { Badge } from '../../components/ui/Badge'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { EntityViewLayout } from '../../components'
import { ExportOverlay } from '../../components/ExportOverlay'
import { EmailExportCheckbox } from '../../components/EmailExportCheckbox'
import { SendEmailModal } from '../../components/SendEmailModal'
import { useBulkWine } from '../../hooks/useBulkWines'
import { useEmailExport } from '../../hooks/useEmailExport'
import { useAuth } from '../../hooks/useAuth'
import { useCountries, useRegions, useVarieties } from '../../hooks/useReferenceData'
import { bulkWinesService } from '../../services/bulk-wines.service'
import { triggerBlobDownload } from '../../utils/download'
import { formatLongDate, formatNumber, formatVolume } from '../../utils/formatters'
import { generateBulkWineEmailContent } from '../../utils/emailContentGenerator'
import {
	buildBulkWineSpecSheetFilename,
	formatLocationLabel,
	getComplianceStatement,
	shouldShowNzSection,
	USER_ROLES,
	type BulkWineFilenameParts,
} from '@ministry-of-wine/shared'

interface Region {
	id: number
	name: string
	countryId: number
}

interface Country {
	id: number
	name: string
}

interface Variety {
	id: number
	name: string
}

interface BulkWineData {
	code: string
	vintage: number | null
	primaryVarietyId: string
	primaryVarietyName: string | null
	supplierId: string
	regionId: string
	priceCurrency: string | null
	pricePerLiter: number | null
	priceMargin: number | null
	priceMarginUnit: '%' | '$'
	totalVolumeLiters: number | null
	technicalAnalysisDate: string | null
	alcoholPercentage: number | null
	totalAcidity: number | null
	phLevel: number | null
	residualSugar: number | null
	volatileAcidity: number | null
	freeSo2: number | null
	totalSo2: number | null
	suitableVegetarians: boolean | null
	suitableVegans: boolean | null
	wineEnriched: boolean | null
	wineSweetened: boolean | null
	exportCertified: boolean | null
	sprayProgrammeFollowed: boolean | null
	calibratedFlowMeter: boolean | null
	coldStable: boolean | null
	proteinStable: boolean | null
	filtered: boolean | null
	filterType: string | null
	filterNote: string | null
	mlfComplete: boolean | null
	oakTreatment: boolean | null
	oakType: string | null
	oakTreatmentNote: string | null
	exportToEu: boolean | null
	exportToUk: boolean | null
	exportToUsa: boolean | null
	exportToChina: boolean | null
	exportToJapan: boolean | null
	exportToCanada: boolean | null
	winerySwinzCertified: boolean | null
	vineyardSwinzCertified: boolean | null
	nzComplianceStatement: boolean | null
	auComplianceStatement: boolean | null
	compositions: Array<{
		varietyId: string
		percentage: string
	}>
}

export const BulkWineDetailPage: React.FC = () => {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const { data: bulkWineResponse, isLoading: loading } = useBulkWine(id as string, Boolean(id))
	const { data: currentUser } = useAuth()
	// Margins and PDF export (download/zip/email) are superadmin-only.
	const isSuperadmin = currentUser?.role === USER_ROLES.SUPERADMIN
	const { data: regionsData } = useRegions()
	const { data: countriesData } = useCountries()
	const { data: varietiesData } = useVarieties()
	const regions = (regionsData || []) as Region[]
	const countries = (countriesData || []) as Country[]
	const varieties = (varietiesData || []) as Variety[]
	const [selectedCountryId, setSelectedCountryId] = useState('')
	const [isExporting, setIsExporting] = useState(false)
	const [isExportSuccess, setIsExportSuccess] = useState(false)
	const [emailMode, setEmailMode] = useState(true)

	const emailExport = useEmailExport()

	const [bulkWine, setBulkWine] = useState<BulkWineData>({
		code: '',
		vintage: null,
		primaryVarietyId: '',
		primaryVarietyName: null,
		supplierId: '',
		regionId: '',
		priceCurrency: 'NZD',
		pricePerLiter: null,
		priceMargin: null,
		priceMarginUnit: '%',
		totalVolumeLiters: null,
		technicalAnalysisDate: null,
		alcoholPercentage: null,
		totalAcidity: null,
		phLevel: null,
		residualSugar: null,
		volatileAcidity: null,
		freeSo2: null,
		totalSo2: null,
		suitableVegetarians: null,
		suitableVegans: null,
		wineEnriched: null,
		wineSweetened: null,
		exportCertified: null,
		sprayProgrammeFollowed: null,
		calibratedFlowMeter: null,
		coldStable: null,
		proteinStable: null,
		filtered: null,
		filterType: null,
		filterNote: null,
		mlfComplete: null,
		oakTreatment: null,
		oakType: null,
		oakTreatmentNote: null,
		exportToEu: null,
		exportToUk: null,
		exportToUsa: null,
		exportToChina: null,
		exportToJapan: null,
		exportToCanada: null,
		winerySwinzCertified: null,
		vineyardSwinzCertified: null,
		nzComplianceStatement: null,
		auComplianceStatement: null,
		compositions: [],
	})

	useEffect(() => {
		if (bulkWineResponse && regions.length > 0) {
			const region = regions.find((r) => r.id === bulkWineResponse.regionId)
			if (region) {
				setSelectedCountryId(String(region.countryId))
			}

			setBulkWine({
				code: bulkWineResponse.name || '',
				vintage: bulkWineResponse.vintage ?? null,
				primaryVarietyId: bulkWineResponse.primaryVarietyId ? String(bulkWineResponse.primaryVarietyId) : '',
				primaryVarietyName: bulkWineResponse.primaryVarietyName ?? null,
				supplierId: bulkWineResponse.supplierId ? String(bulkWineResponse.supplierId) : '',
				regionId: bulkWineResponse.regionId ? String(bulkWineResponse.regionId) : '',
				priceCurrency: bulkWineResponse.priceCurrency ?? 'NZD',
				pricePerLiter: bulkWineResponse.pricePerLiter ?? null,
				priceMargin: bulkWineResponse.priceMargin ?? null,
				priceMarginUnit: bulkWineResponse.priceMarginUnit === '$' ? '$' : '%',
				totalVolumeLiters: bulkWineResponse.totalVolumeLiters ?? null,
				technicalAnalysisDate: bulkWineResponse.technicalAnalysisDate ?? null,
				alcoholPercentage: bulkWineResponse.alcoholPercentage ?? null,
				totalAcidity: bulkWineResponse.totalAcidity ?? null,
				phLevel: bulkWineResponse.phLevel ?? null,
				residualSugar: bulkWineResponse.residualSugar ?? null,
				volatileAcidity: bulkWineResponse.volatileAcidity ?? null,
				freeSo2: bulkWineResponse.freeSo2 ?? null,
				totalSo2: bulkWineResponse.totalSo2 ?? null,
				suitableVegetarians: bulkWineResponse.suitableVegetarians ?? null,
				suitableVegans: bulkWineResponse.suitableVegans ?? null,
				wineEnriched: bulkWineResponse.wineEnriched ?? null,
				wineSweetened: bulkWineResponse.wineSweetened ?? null,
				exportCertified: bulkWineResponse.exportCertified ?? null,
				sprayProgrammeFollowed: bulkWineResponse.sprayProgrammeFollowed ?? null,
				calibratedFlowMeter: bulkWineResponse.calibratedFlowMeter ?? null,
				coldStable: bulkWineResponse.coldStable ?? null,
				proteinStable: bulkWineResponse.proteinStable ?? null,
				filtered: bulkWineResponse.filtered ?? null,
				filterType: bulkWineResponse.filterType ?? null,
				filterNote: bulkWineResponse.filterNote ?? null,
				mlfComplete: bulkWineResponse.mlfComplete ?? null,
				oakTreatment: bulkWineResponse.oakTreatment ?? null,
				oakType: bulkWineResponse.oakType ?? null,
				oakTreatmentNote: bulkWineResponse.oakTreatmentNote ?? null,
				exportToEu: bulkWineResponse.exportToEu ?? null,
				exportToUk: bulkWineResponse.exportToUk ?? null,
				exportToUsa: bulkWineResponse.exportToUsa ?? null,
				exportToChina: bulkWineResponse.exportToChina ?? null,
				exportToJapan: bulkWineResponse.exportToJapan ?? null,
				exportToCanada: bulkWineResponse.exportToCanada ?? null,
				winerySwinzCertified: bulkWineResponse.winerySwinzCertified ?? null,
				vineyardSwinzCertified: bulkWineResponse.vineyardSwinzCertified ?? null,
				nzComplianceStatement: bulkWineResponse.nzComplianceStatement ?? null,
				auComplianceStatement: bulkWineResponse.auComplianceStatement ?? null,
				compositions: bulkWineResponse.compositions || [],
			})
		}
	}, [bulkWineResponse, regions])

	if (loading) {
		return <div className="p-6">Loading...</div>
	}

	const formatInteger = (value: number | null) =>
		value === null || value === undefined
			? 'N/A'
			: Number(value).toLocaleString('en-US', {
					minimumFractionDigits: 0,
					maximumFractionDigits: 0,
				})

	const formatYesNo = (value: boolean | null) => {
		if (value === null || value === undefined) {
			return 'N/A'
		}

		return value ? 'Yes' : 'No'
	}

	const priceBadgeLabel = 'PRICE PER LITER'
	const formattedPricePerLiter =
		bulkWine.pricePerLiter !== null
			? `${bulkWine.priceCurrency || 'NZD'} ${formatNumber(bulkWine.pricePerLiter, 2)}`
			: 'N/A'
	const formattedPriceMargin =
		bulkWine.priceMargin !== null ? `${formatNumber(bulkWine.priceMargin, 2)}${bulkWine.priceMarginUnit}` : 'N/A'
	const regionName = regions.find((r) => String(r.id) === bulkWine.regionId)?.name || 'N/A'
	// Comes straight from the API so retired (soft-deleted) sub-regions still render.
	const subRegionName = bulkWineResponse?.subRegionName || 'N/A'
	// Where the wine is stored (buyer freight estimates), independent of the origin above.
	const locationLabel = formatLocationLabel(
		bulkWineResponse?.locationSubRegionName,
		bulkWineResponse?.locationRegionName,
	)
	const countryName = countries.find((c) => String(c.id) === selectedCountryId)?.name || 'N/A'
	// "Barossa Valley, South Australia, Australia" — sub-region first, omitting anything unset.
	const regionSummary =
		[subRegionName, regionName, countryName].filter((part) => part !== 'N/A').join(', ') || 'N/A'

	// The compliance statement and the "For New Zealand Only" section are
	// region-specific (see @ministry-of-wine/shared). A non-Australian region
	// — including an unknown/missing country — falls back to New Zealand.
	const complianceStatement = getComplianceStatement(countryName)
	const showNzSection = shouldShowNzSection(countryName)
	const volumeSummary = bulkWine.totalVolumeLiters !== null ? `${formatVolume(bulkWine.totalVolumeLiters)} L` : 'N/A'
	const compositionItems = bulkWine.compositions
		.filter((c) => c.varietyId)
		.map((comp) => {
			const variety = varieties.find((v) => String(v.id) === comp.varietyId)
			return variety ? `${variety.name} ${comp.percentage}%` : ''
		})
		.filter(Boolean)

	const handleDownloadPdf = async () => {
		if (!id || isExporting) return

		if (emailMode) {
			// Email mode: open modal with pre-populated content
			try {
				// Convert current bulk wine data to expected format for email generator
				const wineForEmail = {
					id: parseInt(id),
					code: bulkWine.code,
					variety: bulkWine.primaryVarietyName || 'Unknown',
					vintage: bulkWine.vintage?.toString() || 'N/A',
					volume: bulkWine.totalVolumeLiters || 0,
					countryRegion: regionSummary,
					composition: compositionItems.join(', '),
					pricePerLiter: bulkWine.pricePerLiter || 0,
					priceMargin: bulkWine.priceMargin,
					priceMarginUnit: bulkWine.priceMarginUnit,
					priceCurrency: bulkWine.priceCurrency || 'NZD',
					supplierName: bulkWineResponse?.supplierName || 'Unknown',
				}

				const emailContent = generateBulkWineEmailContent(
					[wineForEmail],
					currentUser
						? {
								name: currentUser.name,
								email: currentUser.email,
							}
						: undefined,
				)

				// Build the actual filename that will be generated by the backend
				// Use the same composition logic as compositionItems above
				const compositionsForFilename = bulkWine.compositions
					.filter((c) => c.varietyId)
					.map((comp) => {
						const variety = varieties.find((v) => String(v.id) === comp.varietyId)
						return {
							varietyName: variety?.name || undefined,
							percentage: comp.percentage || undefined,
						}
					})
					.filter((c) => c.varietyName) // Only include if variety name was found

				const filenameParts: BulkWineFilenameParts = {
					code: bulkWine.code || undefined,
					name: bulkWine.name || undefined,
				}
				const filename = buildBulkWineSpecSheetFilename(filenameParts)

				emailExport.openModal({
					subject: emailContent.subject,
					body: emailContent.body,
					attachmentNames: [filename],
					sendFn: async (formData) => {
						await bulkWinesService.emailSingle({
							...formData,
							id: parseInt(id),
						})
					},
				})
			} catch (error) {
				console.error('Failed to prepare email:', error)
				alert('Failed to prepare email. Please try again.')
			}
		} else {
			// Download mode: existing behavior
			setIsExporting(true)
			setIsExportSuccess(false)
			try {
				const { blob, filename } = await bulkWinesService.downloadPdf(id)
				triggerBlobDownload(blob, filename)
				setIsExportSuccess(true)
				window.setTimeout(() => setIsExportSuccess(false), 1000)
			} catch (error) {
				console.error('Failed to download PDF:', error)
				alert('Failed to download PDF. Please try again.')
			} finally {
				setIsExporting(false)
			}
		}
	}

	return (
		<>
			<EntityViewLayout
				closeTo="/inventory/bulk-wine"
				backTo="/inventory/bulk-wine"
				backLabel="Back to Bulk Wine Inventory"
				title={bulkWine.code || 'Bulk Wine'}
				footerActions={
					<>
						{isSuperadmin && (
							<div className="flex items-center gap-3">
								<button
									onClick={handleDownloadPdf}
									disabled={isExporting || isExportSuccess}
									className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#EAECF0] bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isExporting ? 'Exporting...' : 'Download PDF'}
								</button>
								<EmailExportCheckbox checked={emailMode} onChange={setEmailMode} />
							</div>
						)}
						<button
							onClick={() => navigate('/inventory/bulk-wine')}
							className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-[#EAECF0] bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]"
						>
							Close
						</button>
					</>
				}
			>
				<h2 className="text-neutral-900 font-normal text-base leading-5 mb-4">Listing Information</h2>

				<div className="flex gap-2 mb-2">
					<Badge variant="darkCream" size="sm">
						{bulkWine.vintage ?? 'N/A'}
					</Badge>
					<Badge variant="cream" size="sm">
						Bulk Wine
					</Badge>
				</div>

				<div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
					{/* Section 1 */}
					<div>
						<div className="text-[#101828] font-bold text-[25px] leading-[38px] mb-2">{bulkWine.code || 'N/A'}</div>
						<div className="text-[#404040] text-base leading-5 mb-1">{bulkWine.primaryVarietyName || 'N/A'}</div>
						<div className="text-[#404040] text-base leading-5">
							{regionName !== 'N/A' && countryName !== 'N/A' ? regionSummary : 'N/A'}
						</div>
					</div>

					{/* Section 2 */}
					<div className="flex flex-col justify-center items-center gap-2.5 h-[129px] px-[50px] py-[22px] rounded-lg border border-[#EAECF0] bg-white">
						<div className="text-[#404040] text-sm font-normal leading-5">VOLUME</div>
						<div className="text-[#404040] text-base font-bold leading-5">{volumeSummary}</div>
					</div>

					{/* Section 3 */}
					<div className="flex flex-col justify-center items-center gap-2.5 h-[129px] px-[50px] py-[22px] rounded-lg border border-[#EAECF0] bg-white">
						<div className="text-[#404040] text-sm font-normal leading-5">{priceBadgeLabel}</div>
						<div className="text-[#404040] text-base font-bold leading-5">{formattedPricePerLiter}</div>
					</div>
				</div>

				<div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-3">
					<div>
						<div className="flex items-center h-[50px] px-[15px] pr-[48px] py-[6px] bg-[#E3DED9]">
							<h3 className="text-[#101828] text-lg font-bold leading-[38px]">Description</h3>
						</div>
						<div className="flex flex-col items-center gap-[15px] w-full pt-4">
							<table className="w-full [&_td]:align-top">
								<tbody>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Wine Code:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{bulkWine.code || 'N/A'}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Supplier:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{bulkWineResponse?.supplierName || 'N/A'}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Country:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">{countryName}</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Region:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">{regionName}</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Sub-region:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">{subRegionName}</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Location:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">{locationLabel}</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Variety:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{bulkWine.primaryVarietyName || 'N/A'}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Vintage:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{bulkWine.vintage ?? 'N/A'}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Price per Liter:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formattedPricePerLiter}
										</td>
									</tr>
									{isSuperadmin && (
										<tr>
											<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
												Price Margin:
											</td>
											<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
												{formattedPriceMargin}
											</td>
										</tr>
									)}
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Price Currency:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{bulkWine.priceCurrency || 'NZD'}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Volume (Litres):
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">{volumeSummary}</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Composition:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{compositionItems.length > 0 ? (
												<div className="flex flex-col">
													{compositionItems.map((item) => (
														<span key={item}>{item}</span>
													))}
												</div>
											) : (
												'N/A'
											)}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div>
						<div className="flex items-center h-[50px] px-[15px] pr-[48px] py-[6px] bg-[#E3DED9]">
							<h3 className="text-[#101828] text-lg font-bold leading-[38px]">Technical Specifications</h3>
						</div>
						<div className="flex flex-col items-center gap-[15px] w-full pt-4">
							<table className="w-full [&_td]:align-top">
								<tbody>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Analysis Date:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatLongDate(bulkWine.technicalAnalysisDate)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 whitespace-nowrap">
											Calibrated Flow Meter Present:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatYesNo(bulkWine.calibratedFlowMeter)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Cold Stable:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatYesNo(bulkWine.coldStable)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Protein Stable:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatYesNo(bulkWine.proteinStable)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Filtered:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatYesNo(bulkWine.filtered)}
											{bulkWine.filtered && bulkWine.filterType && <span> - {bulkWine.filterType}</span>}
											{bulkWine.filtered && bulkWine.filterNote && (
												<span className="ml-3 inline-block rounded-md bg-[#F6F5F3] px-2.5 py-0.5">
													{bulkWine.filterNote}
												</span>
											)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											MLF Complete:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatYesNo(bulkWine.mlfComplete)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Oak Treatment:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatYesNo(bulkWine.oakTreatment)}
											{bulkWine.oakTreatment && bulkWine.oakType && <span> - {bulkWine.oakType}</span>}
											{bulkWine.oakTreatment && bulkWine.oakTreatmentNote && (
												<span className="ml-3 inline-block rounded-md bg-[#F6F5F3] px-2.5 py-0.5">
													{bulkWine.oakTreatmentNote}
												</span>
											)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">Alcohol:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{bulkWine.alcoholPercentage !== null ? `${formatNumber(bulkWine.alcoholPercentage, 2)}%` : 'N/A'}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 whitespace-nowrap">
											Total Acidity: TA (g/L)
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatNumber(bulkWine.totalAcidity, 2)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">pH:</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatNumber(bulkWine.phLevel, 2)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 whitespace-nowrap">
											Residual Sugar: RS (g/L)
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatNumber(bulkWine.residualSugar, 2)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 whitespace-nowrap">
											Volatile Acidity: VA (g/L)
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatNumber(bulkWine.volatileAcidity, 2)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 whitespace-nowrap">
											Free Sulfur Dioxide: FSO2 (ppm)
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatInteger(bulkWine.freeSo2)}
										</td>
									</tr>
									<tr>
										<td className="w-[134px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 whitespace-nowrap">
											Total Sulfur Dioxide: TSO2 (ppm)
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											{formatInteger(bulkWine.totalSo2)}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>

					<div>
						<div className="flex items-center h-[50px] px-[15px] pr-[48px] py-[6px] bg-[#E3DED9]">
							<h3 className="text-[#101828] text-lg font-bold leading-[38px]">Compliance</h3>
						</div>
						<div className="flex flex-col items-center gap-[15px] w-full pt-4">
							<table className="w-full [&_td]:align-top">
								<tbody>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Suitable for Vegetarians:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.suitableVegetarians)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Suitable for Vegans:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.suitableVegans)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Wine enriched:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.wineEnriched)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Wine Sweetened:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.wineSweetened)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Export Certified:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportCertified)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Spray Programme Followed:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.sprayProgrammeFollowed)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Eligible for Export to EU:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportToEu)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Eligible for Export to UK:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportToUk)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Eligible for Export to USA:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportToUsa)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Eligible for Export to China:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportToChina)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Eligible for Export to Japan:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportToJapan)}
										</td>
									</tr>
									<tr>
										<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
											Eligible for Export to Canada:
										</td>
										<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
											{formatYesNo(bulkWine.exportToCanada)}
										</td>
									</tr>
									<tr>
										<td colSpan={2} className="py-4">
											<hr className="border-t border-neutral-300" />
										</td>
									</tr>
									{showNzSection ? (
										<>
											<tr>
												<td colSpan={2} className="text-[#404040] text-sm font-bold leading-[21px] py-2 px-4">
													For New Zealand Only
												</td>
											</tr>
											<tr>
												<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
													Winery SWNZ Certified:
												</td>
												<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
													{formatYesNo(bulkWine.winerySwinzCertified)}
												</td>
											</tr>
											<tr>
												<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
													Vineyard SWNZ Certified:
												</td>
												<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
													{formatYesNo(bulkWine.vineyardSwinzCertified)}
												</td>
											</tr>
											<tr>
												<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
													New Zealand Compliance Statement:
												</td>
												<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
													{formatYesNo(bulkWine.nzComplianceStatement)}
												</td>
											</tr>
										</>
									) : (
										<>
											<tr>
												<td colSpan={2} className="text-[#404040] text-sm font-bold leading-[21px] py-2 px-4">
													For Australia Only
												</td>
											</tr>
											<tr>
												<td className="w-[220px] text-[#404040] text-sm font-normal leading-[21px] py-2 px-4">
													Australian Compliance Statement:
												</td>
												<td className="text-[#404040] text-sm font-normal leading-[21px] py-2 px-4 text-right">
													{formatYesNo(bulkWine.auComplianceStatement)}
												</td>
											</tr>
										</>
									)}
								</tbody>
							</table>
							<Alert variant="brand" className="mx-4 mt-3 w-auto">
								<CircleCheck className="h-5 w-5" />
								<AlertDescription>{complianceStatement}</AlertDescription>
							</Alert>
						</div>
					</div>
				</div>
			</EntityViewLayout>
			<ExportOverlay
				isExporting={isExporting}
				isSuccess={isExportSuccess}
				loadingMessage="Preparing PDF..."
				successMessage="Download complete"
			/>

			<SendEmailModal
				isOpen={emailExport.isModalOpen}
				onClose={emailExport.closeModal}
				onSend={emailExport.sendEmail}
				defaultSubject={emailExport.modalData?.subject || ''}
				defaultBody={emailExport.modalData?.body || ''}
				attachmentNames={emailExport.modalData?.attachmentNames || []}
				defaultReplyTo={currentUser?.email}
			/>
		</>
	)
}

export default BulkWineDetailPage
